import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/auth';
import type { RegionConfig } from '../../types/AppConfig';
import { SESSION_KEY_REGION } from '../../types/AppConfig';
import RepliCloudLogoDM from '../../assets/RepliCloudLogoDM.svg';
import Spinner from '../../components/migratorDashboard/Spinner';
import { ui } from '../../config/uiSizes';
import { logger } from '../../utils/logger';

const LoginPage: React.FC = () => {
  const { login, authError, clearAuthError, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [regions, setRegions] = useState<RegionConfig[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [configError, setConfigError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoLoginPending, setAutoLoginPending] = useState(false);
  const [regionParamWarning, setRegionParamWarning] = useState<string | null>(null);

  // If the user is already authenticated, skip the login page entirely
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Capture stable references at mount time to avoid stale-closure lint warnings
  const loginRef = useRef(login);
  const searchParamsRef = useRef(searchParams);

  useEffect(() => {
    const stableLogin = loginRef.current;
    // Normalise hyphens to underscores so ?region=us-west-2 matches key "us_west_2"
    const rawRegionParam = searchParamsRef.current.get('region');
    const regionParam = rawRegionParam ? rawRegionParam.replace(/-/g, '_') : null;

    fetch('/config.json')
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load config.json: ${res.status}`);
        return res.json() as Promise<{ regions: RegionConfig[] }>;
      })
      .then((config) => {
        if (!config.regions?.length) throw new Error('config.json is missing regions');
        setRegions(config.regions);

        if (regionParam) {
          const matched = config.regions.find((r) => r.key === regionParam);
          if (matched?.clientId) {
            setAutoLoginPending(true);
            sessionStorage.setItem(
              SESSION_KEY_REGION,
              JSON.stringify({
                key: matched.key,
                label: matched.label,
                loginBaseUri: matched.loginBaseUri,
                clientId: matched.clientId
              })
            );
            stableLogin();
          } else if (matched) {
            setRegionParamWarning(
              `The region "${regionParam}" has no OAuth client ID configured. Please select a region below.`
            );
          } else {
            setRegionParamWarning(
              `The region "${regionParam}" was not recognised. Please select a region below.`
            );
          }
        }
      })
      .catch((err: unknown) => {
        setConfigError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, []);

  const selectedRegion = regions.find((r) => r.key === selectedKey);
  const canLogin = !!selectedKey && !!selectedRegion?.clientId;

  const handleRegionChange = (key: string) => {
    setSelectedKey(key);
    clearAuthError();
  };

  const handleLogin = () => {
    if (!selectedRegion) return;
    logger.info('User selected region for login', {
      region: selectedRegion.key,
      label: selectedRegion.label,
      loginUri: selectedRegion.loginBaseUri
    });
    sessionStorage.setItem(
      SESSION_KEY_REGION,
      JSON.stringify({
        key: selectedRegion.key,
        label: selectedRegion.label,
        loginBaseUri: selectedRegion.loginBaseUri,
        clientId: selectedRegion.clientId
      })
    );
    login();
  };

  return (
    <div className="w-full h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-gray-950 dark:to-gray-900">
      {/* Card */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">

        {/* Header band */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-700 dark:from-gray-900 dark:to-slate-800 px-8 py-8 flex flex-col items-center gap-4">
          {/* Always use the white/inverted logo — looks crisp on the dark band */}
          <img src={RepliCloudLogoDM} alt="RepliCloud" className="h-11 w-auto object-contain brightness-0 invert" />
          {/* Divider */}
          <div className="w-16 h-px bg-slate-500" />
          {/* App name */}
          <p className="text-slate-300 text-sm font-semibold tracking-wider whitespace-nowrap">
            Genesys Cloud Resource Migrator
          </p>
        </div>

        {/* Body */}
        <div className="px-8 py-7">

          {/* Auth error banner */}
          {authError && (
            <div className="mb-5 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/50 px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex gap-2 items-start">
                  <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-red-700 dark:text-red-400">Login failed</p>
                    <p className="text-sm text-red-600 dark:text-red-300 mt-0.5">{authError}</p>
                  </div>
                </div>
                <button
                  onClick={clearAuthError}
                  aria-label="Dismiss error"
                  className="flex-shrink-0 text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-300 transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Loading / auto-login spinner */}
          {(authLoading || loading || autoLoginPending) && (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <Spinner />
              {autoLoginPending && (
                <span className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>
                  Logging you in…
                </span>
              )}
            </div>
          )}

          {/* Config error */}
          {configError && (
            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/50 px-4 py-3 text-sm text-red-600 dark:text-red-400">
              {configError}
            </div>
          )}

          {/* Region selector + login */}
          {!authLoading && !loading && !configError && !autoLoginPending && (
            <>
              {/* Region param warning */}
              {regionParamWarning && (
                <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-4 py-2.5 text-sm text-amber-700 dark:text-amber-400">
                  {regionParamWarning}
                </div>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                Select your Genesys Cloud region, then log in to continue.
              </p>

              <div className="mb-5">
                <label
                  htmlFor="region-select"
                  className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"
                >
                  Region
                </label>
                <div className="relative">
                  <select
                    id="region-select"
                    value={selectedKey}
                    onChange={(e) => handleRegionChange(e.target.value)}
                    className="w-full appearance-none border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 pr-10 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition text-sm"
                  >
                    <option value="" disabled>Select a region…</option>
                    {regions.map(({ key, label, clientId }) => (
                      <option key={key} value={key} disabled={!clientId}>
                        {label}{!clientId ? ' (not configured)' : ''}
                      </option>
                    ))}
                  </select>
                  {/* Custom chevron */}
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m19 9-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {selectedKey && !canLogin && (
                <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-4 py-2.5 text-sm text-amber-700 dark:text-amber-400">
                  This region has no OAuth client ID configured in{' '}
                  <code className="font-mono">public/config.json</code>.
                </div>
              )}

              <button
                onClick={handleLogin}
                disabled={!canLogin}
                className="w-full flex items-center justify-center gap-2 bg-slate-300 text-slate-500 disabled:cursor-not-allowed disabled:shadow-none enabled:bg-blue-600 enabled:hover:bg-blue-700 enabled:text-white enabled:shadow-md enabled:hover:shadow-lg dark:bg-slate-600 dark:text-slate-400 dark:disabled:cursor-not-allowed dark:enabled:bg-blue-600 dark:enabled:hover:bg-blue-500 dark:enabled:text-white font-semibold py-2.5 px-6 rounded-lg transition-all duration-200 text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
                </svg>
                Login with Genesys Cloud
              </button>

              <p className="mt-5 text-xs text-center text-gray-400 dark:text-gray-600">
                Powered by EDCi · RepliCloud
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
